import { Lock, Route } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import type { ArrivalStep } from '../useGuestSession';
import { StepCardRow } from './StepCardRow';

interface Props {
  steps: ArrivalStep[];
  /** Guest-Bearer-Token für die auth-geschützten Bild-URLs. */
  token: string | undefined;
}

/**
 * Die Anfahrt als Karten zum Wischen: jeder Schritt mit Foto und Anfang des Texts, der ganze
 * Schritt samt Bildern und Markierungen im Fenster. Das ersetzt den Aufklapper von 2026-08-07 —
 * die Karten sind nicht zu, aber knapp.
 */
export function ArrivalBlock({ steps, token }: Props) {
  const { t } = useTranslation();
  return (
    <StepCardRow
      steps={steps}
      token={token}
      numbered
      icon={Route}
      labelledBy="arrival-steps-title"
      header={
        <h3
          id="arrival-steps-title"
          className="text-fg-dim font-mono text-[10px] font-medium uppercase tracking-[0.16em]"
        >
          {t('travel.now.arrivalSteps', { count: steps.length })}
        </h3>
      }
      renderExtra={(step) =>
        step.isLocked ? <LockedHint availableFrom={step.availableFrom} /> : null
      }
    />
  );
}

function LockedHint({ availableFrom }: { availableFrom: string | null }) {
  const { t, i18n } = useTranslation();
  const locale = i18n.resolvedLanguage ?? 'de';
  const at = availableFrom ? new Date(availableFrom) : null;
  const hint =
    at && !Number.isNaN(at.getTime())
      ? t('stations.stay.arrival.lockedHint', {
          date: at.toLocaleDateString(locale, {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
          }),
          time: at.toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' }),
        })
      : t('stations.stay.arrival.lockedHintNoTime');
  return (
    <p className="text-fg-mute flex items-center gap-2 text-xs">
      <Lock size={12} aria-hidden />
      <span>{hint}</span>
    </p>
  );
}
